<template>
  <Dialog ref="dialogRef" overflow-visible>
    <template #expander="{ openModal }">
      <a class="cursor-pointer mt-1 add-link" @click="onOpenModal()"> {{ btnTitle }}</a>
    </template>
    <template #dialog.title>
      {{ dialogTitle }}
      <h6 class="mb-3">
        {{ dialogSubtitle }}
      </h6>
      <p class="add-tag-hint">
        All tags are converted to Title Case
      </p>
    </template>
    <template #dialog.content="{ closeModal, closeModalAfter }">
      <!-- TODO (cleaner code): think about removing redundancy between "DeleteItemConfirmationForm" & other forms (e.g. CreateSlideForm) [DSP-N26Dui5A] -->
      <div>
        <StatusBar :errorMessage="errorMessage" class="error-container" />

        <!-- Category Field -->
        <div class="d-flex mb-2">
          <label for="name" class="basis-1/2 text-sm">Category &#9733;</label>
          <div class="basis-1/2 position-relative" width="100%" id="metadataName">
            <TextField
              v-model="metadataInput.name"
              width="100%"
              @focusin="showMetadataNameSuggestions = true"
              @focusout=" showMetadataNameSuggestions = false"
            />
            <div class="autocomplete" v-if="matchingMetadataNames?.length > 0 && showMetadataNameSuggestions">
              <ul class="position-absolute">
                <li
                  v-for="name in matchingMetadataNames"
                  @mousedown="selectName(name)"
                  :class="{ 'selected': name == metadataInput.name }"
                  v-bind:key="name"
                >
                  <span v-html="getBoldSubstring(name, metadataInput.name)"></span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Value Field -->
        <div class="d-flex mb-2">
          <label for="Description" class="basis-1/2 text-sm">Value &#9733;</label>
          <div class="basis-1/2 position-relative" width="100%">
            <TextField
              width="100%"
              v-model="metadataInput.value"
              class="basis-1/2"
              @focusin=" showMetadataValueSuggestions = true"
              @focusout=" showMetadataValueSuggestions = false"
            />
            <div class="autocomplete" v-if="matchingMetaDataValues?.length > 0 && showMetadataValueSuggestions">
              <ul class="position-absolute">
                <li
                  v-for="name in matchingMetaDataValues"
                  @mousedown="selectValue(name)"
                  :class="{ 'selected': name == metadataInput.value }"
                  v-bind:key="name"
                >
                  <span v-html="getBoldSubstring(name, metadataInput.value)"></span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="form-buttons">
          <ButtonCustomized
            label="Cancel"
            @click="closeModal()"
            secondary
          />
          <ButtonCustomized
            label="Save"
            @click="submitForm()"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import Dialog from '../../../components/Dialog/Dialog.vue';
import ButtonCustomized from "../../../components/form-fields/ButtonCustomized.vue";
import { computed, onMounted, ref } from 'vue';
import TextField from "@/components/form-fields/TextField.vue";
import { useForm } from "vee-validate";
import * as yup from 'yup';
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import { searchMetaData, getAllMetadatas } from '@/helpers/api/player/player';
import {
  toTitleCase,
  getBoldSubstring,
  getUniqueItems,
  isEqualLowerCased,
  isIncludeLowerCased
} from '@/helpers/utils';
const emit = defineEmits(['cancel', 'select', 'exit', 'fail'])
const props = defineProps({
  dialogTitle: {
    type: String,
    default: "Select Tag"
  },
  dialogSubtitle: {
    type: String,
    default: ""
  },
  btnTitle: {
    type: String,
    default: "Select Tag"
  },
});

// Core helpers
const metadataItems = ref<any[]>();
const showMetadataNameSuggestions = ref(false);
const showMetadataValueSuggestions = ref(false);

onMounted(async () => {
  metadataItems.value = await getAllMetadatas()
})

// Dialog & Form Helpers
const dialogRef = ref();
defineExpose({
  open: onOpenModal
})

const validationSchema = yup.object({
  name: yup.string().label('Category').required(),
  value: yup.string().label('Value').required(),
});
const { errors, handleSubmit, values: metadataInput } = useForm({ validationSchema });
const errorMessage = ref();
const submitForm = handleSubmit(onSaveSelect, handleSubmitInvalid);
async function handleSubmitInvalid() {
  let errorsAugmentedInSingleMessage = "";
  // TODO (cleaner code): think if there's a cleaner way to write this [DSP-N26Cti4A]
  if (Object.keys(errors.value).length) {
    Object.keys(errors.value).forEach((key) => {
      errorsAugmentedInSingleMessage += errors.value[key] + ". ";
    });
  }
  errorMessage.value = errorsAugmentedInSingleMessage;
}

function onSaveSelect() {
  const selectedMetadataItemPrepared = {
    name: `${metadataInput.name}: ${metadataInput.value}`,
    id: `${metadataInput.name}:${metadataInput.value}`
  }
  emit('select', selectedMetadataItemPrepared);
  errorMessage.value = "";
  dialogRef.value.closeModal()
}

function selectName(name: any) {
  metadataInput.name = name
  showMetadataNameSuggestions.value = false
}
function selectValue(name: any) {
  metadataInput.value = name
  showMetadataValueSuggestions.value = false
}

/** returns "tag names" from matched "tag items" */
const matchingMetadataNames = computed(() => {
  // return empty array if not metadata items available
  if(!metadataItems.value || !metadataItems.value.length) return [];

  // return all tag (metadata) names if no "tag name" or "tag value" inputted
  if(metadataInput.name === '' && metadataInput.value === '') {
    const matches = metadataItems.value.map(item => {
      const [tagName, tagValue] = item.split(':');
      return toTitleCase(tagName);
    })
    return getUniqueItems(matches);
  };

  // continue filtering process
  const matches = metadataItems.value.filter((item) => {
    const [tagName, tagValue] = item.split(':');

    // If no "inputted tag name", pick item as a match if it has the same "tag value" as the currently inputted.
    if(metadataInput.name === '') return isEqualLowerCased(metadataInput.name, tagValue);

    /**
     * If there's "tag name input", pick "tag names" that have "tag name input" as substring,
     * and has the same "tag value" as the "tag value input" if inputted, otherwise ignore it.
     */
    
    const isMatch = isIncludeLowerCased(tagName, metadataInput.name)
      && (
        metadataInput.value === ''
        || isEqualLowerCased(metadataInput.value, tagValue)
      );
    return isMatch;
  }).map(item => {
    const [tagName, tagValue] = item.split(':');
    return toTitleCase(tagName);
  });
  return getUniqueItems(matches);
});

/** returns "tag values" from matched "tag items" */
const matchingMetaDataValues = computed(() => {
  // return empty array if not metadata items available
  if(!metadataItems.value || !metadataItems.value.length) return [];

  // return all tag (metadata) names if no "tag name" or "tag value" inputted
  if(metadataInput.name === '' && metadataInput.value === '') {
    const matches = metadataItems.value.map(item => {
      const [tagName, tagValue] = item.split(':');
      return toTitleCase(tagValue);
    })
    return getUniqueItems(matches);
  };

  // continue filtering process
  const matches = metadataItems.value.filter((item) => {
    const [tagName, tagValue] = item.split(':');

    // If no "tag value input", pick item as a match if it has the same "tag value" as the currently inputted.
    if(metadataInput.value === '') return isEqualLowerCased(metadataInput.name, tagName);

    /**
     * If there's "tag value input", pick "tag values" that have "tag value input" as substring,
     * and has the same "tag name" as the "tag name input" if inputted, otherwise ignore it.
     */
    const isMatch = isIncludeLowerCased(tagValue, metadataInput.value)
      && (
        metadataInput.name === ''
        || isEqualLowerCased(metadataInput.name, tagName)
      );
    return isMatch;
  }).map(item => {
    const [tagName, tagValue] = item.split(':');
    return toTitleCase(tagValue);
  });
  return getUniqueItems(matches);
});

function onOpenModal() {
  resetValues()
  dialogRef.value.openModal()
}

function resetValues() {
  metadataInput.name = '';
  metadataInput.value = '';
  showMetadataNameSuggestions.value = false;
  showMetadataValueSuggestions.value = false;
}
</script>

<style lang="scss" scoped>
.button {
  cursor: pointer;
}

.delete-item-form {
  display: flex;
  flex-direction: column;
  max-width: 30rem;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.disabled {
  pointer-events: none;
  background-color: #E6E6E6;
  color: #999999
}

.autocomplete {
  left: 0;
  position: absolute;
  width: 100%;
  z-index: 2;
}

.autocomplete ul {
  background: none repeat scroll 0 0 #ffffff;
  border-top: 1px dotted #ccc;
  border-bottom: 1px solid #ccc;
  margin-top: 0;
  padding-bottom: 0;
  padding-left: 0;
  position: relative;
  top: -1px;
  list-style: none;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  width: fit-content;
  min-width: 100%;
  // min-width: 24ch
}

.autocomplete ul li {
  background: inherit;
  border-color: #ccc;
  border-image: none;
  border-style: solid;
  border-width: 0 1px;
  display: block;
  font-size: 14px;
  font-weight: normal;
  padding: 0px 12px;
  cursor: pointer;
}

.autocomplete ul li:hover {
  background-color: #ebebeb;
}

.autocomplete ul li.selected {
  background-color: #ebebeb;
}

.add-tag-hint {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  width: 100%;
  background-color: #e6e6e6;
  padding: 0.5rem;
}

.error-container {
  max-width: 15.6rem;
  margin-bottom: 0.7rem;
}

.add-link {
  border: 0;
  cursor: pointer;
  display: inline-block;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 6px;
  text-transform: uppercase;
  color: rgba(37, 41, 74, 0.5);
  font-weight: bold;
}

.add-link:hover {
  text-decoration: underline;
}
</style>